// react
import { ChangeEvent, useState } from 'react';

// i81n
import { useTranslation } from 'react-i18next';

// types
import {
	Permission,
	PermissionOperation,
	PermissionResource,
	PermissionResourceV2,
	Permissions,
} from '../../../@types';
import { permissionResourcesV2 } from '../../../@types/unions';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Grid, InputLabel, SelectChangeEvent } from '@mui/material';

// fields
import { Select } from '../../fields/Select/Select';

// tables
import { TableBasicPermissions } from '../../tables/basic/TableBasicPermissions/TableBasicPermissions';

// utils
import {
	permissionOperationMap,
	permissionResourceMap,
	permissionsReadOnly,
	permissionsReadWrite,
} from '../../../utils/permissions';
import { translatePermissionResource } from '../../../utils/translations';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export type PermissionsTemplate = 'CUSTOM' | 'READ_ONLY' | 'READ_WRITE';

export interface ValuesProps {
	permissions: Permissions;
	template: PermissionsTemplate;
}

export interface UserPermissionsProps {
	defaultPermissions?: Permissions;
	defaultTemplate?: PermissionsTemplate;
	onChange: (e: ChangeEvent | SelectChangeEvent<unknown>, permissions: Permissions) => void;
}

export const UserPermissions = ({
	defaultPermissions = permissionsReadWrite,
	defaultTemplate = 'READ_WRITE',
	onChange,
}: UserPermissionsProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// state
	const [permissions, setPermissions] = useState<Permission[]>([]);
	const [values, setValues] = useState<ValuesProps>({
		permissions: defaultPermissions,
		template: defaultTemplate,
	});

	// fetch permissions
	useFetch({
		url: `${process.env.REACT_APP_API_URL}/permissions`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				let permissionsNew: Permission[] = res.data;
				if (!featureFlags?.ACTIVITIES) {
					permissionsNew = permissionsNew.filter((p) => p.resource !== 'ACT');
				}
				setPermissions(permissionsNew);
			}
		},
	});

	const resources = [...new Set(permissions.map((p) => p.resource))]
		.sort((a, b) => (a > b ? 1 : -1))
		.filter((r) => !permissionResourcesV2.includes(r as PermissionResourceV2));

	const permissionsByResource = resources
		.map((resource) => {
			const resourcePermissions = permissions.filter((p) => p.resource === resource);

			return {
				label: translatePermissionResource(resource, t),
				resource,
				permissions: resourcePermissions,
			};
		})
		.sort((a, b) => {
			return a.label.localeCompare(b.label);
		});

	const handleChangePermissionsTemplate = (e: SelectChangeEvent<unknown>) => {
		const { value } = e.target as HTMLInputElement;

		let permissionsNew = { ...values.permissions };

		if (value === 'READ_WRITE') {
			permissionsNew = { ...permissionsReadWrite };
		} else if (value === 'READ_ONLY') {
			permissionsNew = { ...permissionsReadOnly };
		}

		onChange(e, permissionsNew);

		setValues((prev) => ({
			...prev,
			permissions: permissionsNew,
			template: value as PermissionsTemplate,
		}));
	};

	const handleChangePermission = (
		e: ChangeEvent,
		resource: PermissionResource,
		operation: PermissionOperation
	) => {
		const { checked } = e.target as HTMLInputElement;

		const valuesNew = {
			...values,
			permissions: {
				...values.permissions,
				[permissionResourceMap[resource]]: {
					...values.permissions[permissionResourceMap[resource]],
					[permissionOperationMap[operation]]: checked,
				},
			},
		};

		onChange(e, valuesNew.permissions);

		setValues(valuesNew);
	};

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Grid alignItems="flex-end" container justifyContent="space-between" spacing={2}>
					<Grid item>
						<InputLabel required={true}>{t('permissions')}</InputLabel>
					</Grid>
					<Grid item>
						<Select
							hasNoneOption={false}
							name="permissionsTemplate"
							onChange={handleChangePermissionsTemplate}
							options={[
								{
									label: t('custom'),
									value: 'CUSTOM',
								},
								{
									label: t('readAndWrite'),
									value: 'READ_WRITE',
								},
								{
									label: t('readOnly'),
									value: 'READ_ONLY',
								},
							]}
							size="small"
							value={values.template}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12}>
				<TableBasicPermissions
					data={permissionsByResource}
					onChange={handleChangePermission}
					values={values.permissions}
				/>
			</Grid>
		</Grid>
	);
};
